import './SupplyDemand.scss';
import React, { useMemo, useState } from 'react';
import { Row, Col, Select, Statistic, Space, Alert, Descriptions, Button } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined } from '@ant-design/icons';
import { LoadingIndicator, ErrorAlert, SupplyDemandCharts, ExportDialog } from '../components';
import { useSupplyDemandData } from '../hooks/useIndustryData';
import {
  calculateSupplyDemandGap,
  calculateInventoryConsumptionRatio,
  calculateMonthlyConsumption,
  calculateSupplyDemandRatio,
  formatSupplyDemandGap,
} from '../utils/supplyDemandUtils';
import type { SupplyDemandData } from '../types';

const { Option } = Select;

const SupplyDemand: React.FC = () => {
  // ... (logic remains same)
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(undefined);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);

  // 构建查询参数
  const queryParams = useMemo(() => {
    const params: any = {};
    if (selectedYear) params.year = selectedYear;
    return params;
  }, [selectedYear]);

  // 从 hook 获取数据
  const { data: supplyDemandData, isLoading, error, refetch } = useSupplyDemandData(queryParams);
  const allData = supplyDemandData?.data || [];

  // 筛选逻辑... (keep exiting logic)
  const filteredData = React.useMemo(() => {
    if (!allData) return [];
    return allData.filter((d: SupplyDemandData) => !selectedCountry || d.country === selectedCountry);
  }, [allData, selectedCountry]);

  // Unique Lists logic... (keep existing)
  const countries = React.useMemo(() => {
    if (!allData) return [];
    const countrySet = new Set(allData.map((d: SupplyDemandData) => d.country));
    return Array.from(countrySet).sort();
  }, [allData]);

  const years = React.useMemo(() => {
    if (!allData) return [];
    const yearSet = new Set(allData.map((d: SupplyDemandData) => d.year));
    return (Array.from(yearSet) as number[]).sort((a, b) => b - a);
  }, [allData]);

  // Indicators calculation... (keep existing)
  const indicators = useMemo(() => {
    if (filteredData.length === 0) {
      return { totalSupply: 0, totalDemand: 0, gap: 0, gapPercentage: 0, ratio: 0, inventoryRatio: 0 };
    }
    const totalSupply = filteredData.reduce((sum: number, d: SupplyDemandData) => sum + d.supply_tons, 0);
    const totalDemand = filteredData.reduce((sum: number, d: SupplyDemandData) => sum + d.demand_tons, 0);
    const totalInventory = filteredData.reduce((sum: number, d: SupplyDemandData) => sum + (d.inventory_tons || 0), 0);
    const gap = calculateSupplyDemandGap(totalSupply, totalDemand);
    const gapPercentage = totalDemand > 0 ? (gap / totalDemand) * 100 : 0;
    const ratio = calculateSupplyDemandRatio(totalSupply, totalDemand);
    const monthlyConsumption = calculateMonthlyConsumption(totalDemand);
    const inventoryRatio = calculateInventoryConsumptionRatio(totalInventory, monthlyConsumption);
    return {
      totalSupply: Math.round(totalSupply),
      totalDemand: Math.round(totalDemand),
      gap: Math.round(gap),
      gapPercentage: gapPercentage.toFixed(2),
      ratio: ratio.toFixed(2),
      inventoryRatio: inventoryRatio.toFixed(1),
    };
  }, [filteredData]);

  const countryData = useMemo(() => {
    if (!selectedCountry || filteredData.length === 0) return null;
    const latestYear = Math.max(...filteredData.map((d: SupplyDemandData) => d.year));
    return filteredData.find((d: SupplyDemandData) => d.year === latestYear);
  }, [selectedCountry, filteredData]);


  if (isLoading) return <LoadingIndicator loading={true} tip="加载供需数据..." />;
  if (error) return <ErrorAlert message="加载失败" description="无法加载供需数据" onRetry={refetch} />;

  // Export fields... (keep existing)
  const supplyDemandExportFields = [
    { value: 'year', label: '年份' },
    { value: 'country', label: '国家/地区' },
    { value: 'supply_tons', label: '供应量（吨）' },
    { value: 'demand_tons', label: '需求量（吨）' },
    { value: 'inventory_tons', label: '库存量（吨）' },
    { value: 'import_tons', label: '进口量（吨）' },
    { value: 'export_tons', label: '出口量（吨）' },
    { value: 'demand_by_application', label: '需求分布' },
  ];

  return (
    <div className="supply-demand-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>全球钒产量和供需分析</h1>
        <Button type="primary" icon={<DownloadOutlined />} onClick={() => setExportDialogVisible(true)} className="glass-button">
          导出数据
        </Button>
      </div>

      {/* Filter Section - Glass Card */}
      <div className="glass-card filter-section">
        <div>
          <span style={{ marginRight: '8px', color: '#1f1f1f' }}>国家/地区：</span>
          <Select
            style={{ width: 200 }}
            placeholder="全球（所有国家）"
            allowClear
            value={selectedCountry}
            onChange={setSelectedCountry}
            popupClassName="glass-dropdown" // Need to ensure dropdown styles match if possible, or accept default
          >
            {countries.map(c => <Option key={String(c)} value={String(c)}>{String(c)}</Option>)}
          </Select>
        </div>
        <div>
          <span style={{ marginRight: '8px', color: '#1f1f1f' }}>年份：</span>
          <Select
            style={{ width: 150 }}
            placeholder="全部年份"
            allowClear
            value={selectedYear}
            onChange={setSelectedYear}
          >
            {years.map(y => <Option key={String(y)} value={String(y)}>{String(y)}</Option>)}
          </Select>
        </div>
      </div>

      {/* Indicators - Glass Cards Grid */}
      <Row gutter={24} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <div className="glass-card" style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic title="总供应量" value={indicators.totalSupply} suffix="吨" valueStyle={{ color: '#1890ff' }} />
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card" style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic title="总需求量" value={indicators.totalDemand} suffix="吨" valueStyle={{ color: '#52c41a' }} />
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card" style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="供需缺口"
              value={Math.abs(indicators.gap)}
              suffix="吨"
              valueStyle={{ color: indicators.gap >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {indicators.gap >= 0 ? <ArrowUpOutlined style={{ color: '#52c41a' }} /> : <ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
              <span>{formatSupplyDemandGap(indicators.gap)} ({indicators.gapPercentage}%)</span>
            </div>
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card" style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="供需比率"
              value={indicators.ratio}
              precision={2}
              valueStyle={{ color: parseFloat(String(indicators.ratio)) >= 1 ? '#52c41a' : '#ff4d4f' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              {parseFloat(String(indicators.ratio)) >= 1 ? '供应充足' : '供应紧张'}
            </div>
          </div>
        </Col>
      </Row>

      {/* Inventory Ratio - Conditional */}
      {parseFloat(String(indicators.inventoryRatio)) > 0 && (
        <div className="glass-card">
          <Statistic
            title="库存消费比"
            value={indicators.inventoryRatio}
            suffix="个月"
            valueStyle={{ color: '#1890ff' }}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
            当前库存可支撑 {indicators.inventoryRatio} 个月的消费
          </div>
        </div>
      )}

      {/* Country Details */}
      {countryData && (
        <div className="glass-card">
          <h3 style={{ color: '#1890ff', marginBottom: '16px' }}>{selectedCountry} 详细信息 ({countryData.year})</h3>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="年份">{countryData.year}</Descriptions.Item>
            <Descriptions.Item label="国家">{countryData.country}</Descriptions.Item>
            <Descriptions.Item label="产量">{countryData.supply_tons.toLocaleString()} 吨</Descriptions.Item>
            <Descriptions.Item label="需求量">{countryData.demand_tons.toLocaleString()} 吨</Descriptions.Item>
            {countryData.inventory_tons && <Descriptions.Item label="库存量">{countryData.inventory_tons.toLocaleString()} 吨</Descriptions.Item>}
            {countryData.import_tons && <Descriptions.Item label="进口量">{countryData.import_tons.toLocaleString()} 吨</Descriptions.Item>}
            {countryData.export_tons && <Descriptions.Item label="出口量">{countryData.export_tons.toLocaleString()} 吨</Descriptions.Item>}
            {countryData.demand_by_application && (
              <Descriptions.Item label="需求分布" span={2}>
                {Object.entries(countryData.demand_by_application).map(([app, demand]) => (
                  <div key={app} style={{ marginBottom: 4 }}>
                    <span style={{ color: '#666' }}>{app}:</span> {(demand as number).toLocaleString()} 吨
                  </div>
                ))}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      )}

      {/* Charts Section - Passing theme prop if supported or handling via CSS */}
      {filteredData.length > 0 ? (
        <div className="charts-container">
          <SupplyDemandCharts data={filteredData} />
        </div>
      ) : (
        <Alert message="暂无数据" description="当前筛选条件下没有找到供需数据" type="info" showIcon />
      )}

      <div className="glass-card" style={{ marginTop: '24px' }}>
        <h4 style={{ color: '#1f1f1f', marginBottom: '12px' }}>指标说明</h4>
        <Space direction="vertical" size="middle" style={{ color: '#666' }}>
          <div><strong>供需缺口：</strong>供应量 - 需求量。正值表示供应过剩，负值表示供应不足。</div>
          <div><strong>供需比率：</strong>供应量 / 需求量。大于1表示供应充足，小于1表示供应紧张。</div>
          <div><strong>库存消费比：</strong>库存量 / 月均消费量。表示当前库存可以支撑多少个月的消费。</div>
          <div style={{ fontSize: '12px', opacity: 0.5 }}>* 数据来源：USGS、行业报告等公开数据源</div>
        </Space>
      </div>

      <ExportDialog
        visible={exportDialogVisible}
        onClose={() => setExportDialogVisible(false)}
        dataType="production"
        availableFields={supplyDemandExportFields}
        currentFilters={{ country: selectedCountry, year: selectedYear }}
      />
    </div>
  );
};

export default SupplyDemand;
